import { useState, useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowRight, ChevronDown } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1767950470198-c9cd97f8ed87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB2aWxsYSUyMHBvb2wlMjBldmVuaW5nfGVufDB8fHx8MTc3Mzc1NTA1MXww&ixlib=rb-4.1.0&q=85";
const INTERIOR_IMG = "https://images.unsplash.com/photo-1678686425633-84fc103e2727?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb20lMjBmaXJlcGxhY2UlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzczNzU1MDU2fDA&ixlib=rb-4.1.0&q=85";

const TIERS = [
  {
    id: "selected",
    name: "SELECTED",
    price: "12,500",
    priceNum: 12500,
    description: "Curated visibility within the Soulful Places ecosystem",
    features: [
      "Curated listing on Soulful Places",
      "Inclusion in private estate & buyout collections",
      "Core property positioning",
      "Basic editorial introduction",
      "Access to the Soulful Places ecosystem",
      "Qualified lead introduction capability",
    ],
    recommended: false,
  },
  {
    id: "signature",
    name: "SIGNATURE",
    price: "18,000",
    priceNum: 18000,
    description: "Enhanced narrative and strategic positioning for discerning properties",
    features: [
      "Everything in Selected",
      "Priority placement across collections",
      "Enhanced narrative & storytelling development",
      "Dedicated editorial feature",
      "1 remote podcast / interview session",
      "Strategic positioning for buyout & executive retreat use cases",
      "Increased visibility within qualified buyer network",
    ],
    recommended: true,
  },
  {
    id: "icon",
    name: "ICON",
    price: "28,000",
    priceNum: 28000,
    description: "Maximum presence, premium integration, and flagship positioning",
    features: [
      "Everything in Signature",
      "Premium placement & elevated visibility",
      "Signature brand narrative integration",
      "Enhanced editorial & media integration",
      "Priority access to curated buyer & MICE-oriented introductions",
      "Custom positioning for private estate, heli-arrival, retreat & buyout experiences",
      "Preferred consideration for future flagship campaigns",
    ],
    recommended: false,
  },
];

const AWARDS = [
  "Soulful Places Award",
  "Longevity Award",
  "Architecture & Design Award",
];

const Divider = () => (
  <div className="flex items-center justify-center py-12 md:py-16">
    <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#C9A96E]/40 to-transparent" />
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[#C9A96E] text-[10px] md:text-xs uppercase tracking-[0.35em] font-light mb-4 md:mb-6">{children}</p>
);

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function NeituriProposal() {
  const [selectedTier, setSelectedTier] = useState("signature");
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 180]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  useEffect(() => {
    document.title = "Neituri \u00b7 Soulful Places Partnership";
  }, []);

  return (
    <div className="min-h-screen bg-[#030303] text-white antialiased" data-testid="neituri-proposal">
      {/* ─── HERO ─── */}
      <section ref={heroRef} className="relative h-[85vh] md:h-[90vh] overflow-hidden" data-testid="neituri-hero">
        <motion.div style={{ y: heroY }} className="absolute inset-0 will-change-transform">
          <img
            src={HERO_IMG}
            alt="Neituri Estate"
            className="w-full h-full object-cover"
            style={{ transform: "scale(1.08)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/50 to-[#030303]/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#030303]/40 via-transparent to-[#030303]/40" />
        </motion.div>

        <motion.div
          style={{ opacity: heroOpacity }}
          className="absolute inset-0 flex flex-col items-center justify-end pb-20 md:pb-28 px-6 z-10"
        >
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-[#C9A96E] text-[10px] md:text-xs uppercase tracking-[0.45em] font-light mb-5"
          >
            Private Partnership Proposal
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl sm:text-5xl lg:text-7xl font-light tracking-wide text-center"
            style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}
          >
            Neituri
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="text-white/40 text-sm md:text-base mt-4 tracking-widest font-light"
          >
            by Natory Manor
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="absolute bottom-8"
          >
            <ChevronDown className="w-5 h-5 text-white/30 animate-bounce" />
          </motion.div>
        </motion.div>
      </section>

      {/* ─── MAIN CONTENT ─── */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8">

        {/* ═══ SECTION 1: INTRO ═══ */}
        <Divider />
        <section data-testid="section-intro">
          <FadeIn>
            <SectionLabel>Introduction</SectionLabel>
            <h2
              className="text-2xl md:text-3xl lg:text-4xl font-light leading-snug mb-8"
              style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}
            >
              A selective ecosystem for<br />
              exceptional hospitality.
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="space-y-5 text-white/55 text-[15px] md:text-base leading-relaxed font-light">
              <p>
                Soulful Places is an invitation-led ecosystem that connects discerning travellers with the world's most
                meaningful hospitality experiences. We are not an OTA. We are not a mass platform. We are a curated
                membership community built on storytelling, trust, and selective visibility.
              </p>
              <p>
                Our focus is on properties that offer something beyond a room &mdash; a sense of place, an intentional
                experience, a story worth telling. Every partner is chosen deliberately, and every narrative is developed
                individually.
              </p>
              <p>
                For private estates and buyout-led concepts like Neituri, Soulful Places provides precisely the kind
                of controlled, high-quality visibility that mass-market channels cannot deliver: qualified introductions,
                narrative-first positioning, and access to a network of discerning buyers who understand the value of
                exclusivity.
              </p>
            </div>
          </FadeIn>
        </section>

        {/* ═══ SECTION 2: WHY NEITURI FITS ═══ */}
        <Divider />
        <section data-testid="section-why-neituri">
          <FadeIn>
            <SectionLabel>Why Neituri</SectionLabel>
            <h2
              className="text-2xl md:text-3xl lg:text-4xl font-light leading-snug mb-8"
              style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}
            >
              A natural alignment.
            </h2>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="relative rounded-2xl overflow-hidden mb-10 aspect-[21/9]">
              <img src={INTERIOR_IMG} alt="Neituri Interior" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030303]/70 to-transparent" />
            </div>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="space-y-5 text-white/55 text-[15px] md:text-base leading-relaxed font-light">
              <p>
                Neituri represents a category of hospitality that demands a different approach to visibility. As a
                highly exclusive, privacy-led private estate, it does not compete on volume &mdash; it competes on
                meaning, intimacy, and experience.
              </p>
              <p>
                The property is ideal for private gatherings, executive retreats, VIP corporate use, and intimate
                celebrations &mdash; occasions where discretion and quality of service are non-negotiable.
              </p>
              <p>
                With limited capacity and a buyout-first model, Neituri is precisely the kind of property that thrives
                within a curated, story-led ecosystem rather than on traditional booking channels. Soulful Places
                provides the platform for that positioning.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
              {[
                { label: "Exclusive", sub: "Buyout-led concept" },
                { label: "Private", sub: "Discretion guaranteed" },
                { label: "Curated", sub: "Story-first positioning" },
                { label: "Premium", sub: "Qualified audience" },
              ].map((item, i) => (
                <div key={i} className="text-center py-5 border border-white/[0.06] rounded-xl">
                  <p className="text-white/80 text-sm font-medium tracking-wide">{item.label}</p>
                  <p className="text-white/25 text-[11px] mt-1">{item.sub}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </section>

        {/* ═══ SECTION 3: ANNUAL CORE MEMBERSHIPS ═══ */}
        <Divider />
        <section data-testid="section-memberships">
          <FadeIn>
            <SectionLabel>Annual Core Membership</SectionLabel>
            <h2
              className="text-2xl md:text-3xl lg:text-4xl font-light leading-snug mb-3"
              style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}
            >
              Three tiers. One standard.
            </h2>
            <p className="text-white/35 text-sm font-light mb-12 max-w-xl">
              Each membership tier also serves as your visible partner category on the Soulful Places platform.
              Choose the level of presence that matches your vision.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-4">
            {TIERS.map((tier, index) => {
              const isSelected = selectedTier === tier.id;
              const isRec = tier.recommended;
              return (
                <FadeIn key={tier.id} delay={0.08 * index}>
                  <div
                    onClick={() => setSelectedTier(tier.id)}
                    className={`relative cursor-pointer rounded-2xl transition-all duration-500 ${
                      isRec
                        ? "bg-gradient-to-b from-[#C9A96E]/[0.07] to-transparent border border-[#C9A96E]/20"
                        : "bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12]"
                    } ${isSelected ? "ring-1 ring-[#C9A96E]/40" : ""}`}
                    data-testid={`tier-${tier.id}`}
                  >
                    {isRec && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                        <span className="px-4 py-1 bg-[#C9A96E] text-[#030303] text-[10px] font-semibold uppercase tracking-widest rounded-full">
                          Recommended
                        </span>
                      </div>
                    )}

                    <div className="p-6 md:p-7 pt-8">
                      {/* Tier Name */}
                      <p className={`text-xs tracking-[0.3em] font-medium mb-6 ${isRec ? "text-[#C9A96E]" : "text-white/40"}`}>
                        {tier.name}
                      </p>

                      {/* Price */}
                      <div className="mb-2">
                        <span
                          className="text-3xl md:text-4xl font-light text-white"
                          style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}
                        >
                          &euro;{tier.price}
                        </span>
                      </div>
                      <p className="text-white/25 text-xs tracking-wider mb-6">per year</p>

                      {/* Description */}
                      <p className="text-white/35 text-[13px] leading-relaxed mb-7 min-h-[40px]">
                        {tier.description}
                      </p>

                      {/* Divider */}
                      <div className="w-full h-px bg-white/[0.06] mb-6" />

                      {/* Features */}
                      <ul className="space-y-3">
                        {tier.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${isRec ? "text-[#C9A96E]/70" : "text-white/25"}`} />
                            <span className="text-white/50 text-[13px] leading-snug font-light">{f}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Select Button */}
                      <button
                        className={`w-full mt-8 py-3 rounded-xl text-sm font-medium tracking-wide transition-all duration-300 ${
                          isSelected
                            ? isRec
                              ? "bg-[#C9A96E] text-[#030303]"
                              : "bg-white/90 text-[#030303]"
                            : isRec
                              ? "bg-[#C9A96E]/10 text-[#C9A96E] hover:bg-[#C9A96E]/20"
                              : "bg-white/[0.04] text-white/50 hover:bg-white/[0.08]"
                        }`}
                        data-testid={`select-tier-${tier.id}`}
                      >
                        {isSelected ? "Selected" : "Select"}
                      </button>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </section>

        {/* ═══ SECTION 4: OPTIONAL FUTURE ADD-ONS ═══ */}
        <Divider />
        <section data-testid="section-addons">
          <FadeIn>
            <SectionLabel>Optional Future Add-Ons</SectionLabel>
            <h2
              className="text-2xl md:text-3xl font-light leading-snug mb-8"
              style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}
            >
              Beyond the membership.
            </h2>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="space-y-5 text-white/45 text-[15px] leading-relaxed font-light mb-10">
              <p>
                In addition to the annual core membership, Soulful Places offers optional production and
                recognition services that can be explored once the partnership is established:
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              {[
                {
                  title: "On-Site Storytelling Production",
                  desc: "Professional on-location content creation capturing the essence and narrative of your property.",
                },
                {
                  title: "On-Site Walkthrough Filming",
                  desc: "Cinematic property walkthrough designed for qualified buyer presentations and editorial use.",
                },
                {
                  title: "Future Awards Programme",
                  desc: "Eligibility for recognition through the Soulful Places awards, subject to on-site evaluation.",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="p-6 rounded-xl border border-white/[0.06] bg-white/[0.015]"
                >
                  <p className="text-white/70 text-sm font-medium mb-2">{item.title}</p>
                  <p className="text-white/30 text-[13px] leading-relaxed font-light">{item.desc}</p>
                </div>
              ))}
            </div>
          </FadeIn>

          {/* Awards Notice */}
          <FadeIn delay={0.2}>
            <div className="border-l-2 border-[#C9A96E]/30 pl-6 py-2">
              <p className="text-white/30 text-[11px] uppercase tracking-[0.2em] mb-3">Future Awards</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {AWARDS.map((a, i) => (
                  <span key={i} className="px-3 py-1 rounded-full border border-white/[0.08] text-white/40 text-[12px] font-light">
                    {a}
                  </span>
                ))}
              </div>
              <p className="text-white/35 text-[13px] leading-relaxed font-light italic">
                Recognition through our awards is only possible after an on-site visit and in-person evaluation
                by Soulful Places.
              </p>
              <p className="text-white/25 text-[12px] mt-2 font-light">
                Awards are not part of the annual membership. They are optional future recognitions only.
              </p>
            </div>
          </FadeIn>
        </section>

        {/* ═══ SECTION 5: NEXT STEP ═══ */}
        <Divider />
        <section className="pb-20 md:pb-28" data-testid="section-next-step">
          <FadeIn>
            <div className="text-center max-w-lg mx-auto">
              <SectionLabel>Next Step</SectionLabel>
              <h2
                className="text-2xl md:text-3xl font-light leading-snug mb-6"
                style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}
              >
                Let us begin.
              </h2>
              <p className="text-white/40 text-[15px] leading-relaxed font-light mb-10">
                The next step is to select your preferred annual membership tier and align on
                onboarding, positioning, and storytelling rollout. We look forward to welcoming
                Neituri into the Soulful Places community.
              </p>

              {selectedTier && (
                <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08] mb-8">
                  <span className="text-white/30 text-xs">Your selection:</span>
                  <span className="text-[#C9A96E] text-sm font-medium tracking-wider uppercase">
                    {TIERS.find(t => t.id === selectedTier)?.name}
                  </span>
                  <span className="text-white/20 text-xs">
                    &euro;{TIERS.find(t => t.id === selectedTier)?.price} / year
                  </span>
                </div>
              )}

              <div>
                <a
                  href="mailto:hello@soulfulplaces.com?subject=Neituri%20Partnership%20%E2%80%93%20Membership%20Selection"
                  className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#C9A96E] text-[#030303] rounded-xl text-sm font-medium tracking-wide hover:bg-[#D4B87A] transition-colors duration-300"
                  data-testid="cta-button"
                >
                  Confirm Interest
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              <p className="text-white/20 text-[11px] mt-6 font-light">
                Or reach us directly at hello@soulfulplaces.com
              </p>
            </div>
          </FadeIn>
        </section>

        {/* ─── FOOTER ─── */}
        <div className="border-t border-white/[0.05] py-10 text-center">
          <p className="text-white/15 text-[11px] tracking-[0.2em] uppercase font-light">
            Soulful Places &middot; Private Partnership Proposal &middot; Neituri
          </p>
        </div>
      </div>
    </div>
  );
}
